import { UseForm, useForm } from '@brenger/react';
import { push } from 'connected-react-router';
import * as React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import '../../../../assets/user/user.scss';
import {
  Button,
  FlashMessage,
  Heading,
  InputSwitch,
  SectionWrapper,
  SheetLayoutContent,
  SheetLayoutContentWrapper,
  SheetLayoutHeader,
} from '../../../../brenger-shared-ui';
import { Col, Row } from '../../../../brenger-shared-ui/components/layout/grid';
import { useTypedSelector } from '../../../../hooks';
import { r } from '../../../../routes';
import { urlParser } from '../../../../utils/basics';
import { runValidators } from '../../../../utils/validation';
import SocialButtons from '../../components/SocialButtons';
import { actions } from '../../ducks';
import { CompanySchema } from '../../schema';
import { Company, UserProperties } from '../../typings';
import CompanyDetails from './CompanyDetails';
import PersonalDetails from './PersonalDetails';
import { AccountType, buildRegisterSubmitPayload, RegisterFormFields } from './types';
import { personValidators, RegisterFormState, runCompanyValidators } from './validators';

export const RegisterPage: React.FC = () => {
  const dispatch = useDispatch();
  const submitting = useTypedSelector(state => state.user.loading);
  const errors = useTypedSelector(state => state.user.errors);
  const userId = useTypedSelector(state => state.user.id);
  const userDetails = useTypedSelector(state => state.user.details);

  const form = useForm({
    initialState: {
      type: urlParser().get('business') ? 'business' : 'private',
      first_name: '',
      last_name: '',
      email: '',
      new_password: '',
      phone: '',
      termsofuse: false,
      account_name: '',
      address_line1: '',
      postal_code: '',
      locality: '',
      country_code: 'NL',
    },
    validators: {
      type: () => false,
      first_name: val => runValidators(val, personValidators(UserProperties.FIRST_NAME)),
      last_name: val => runValidators(val, personValidators(UserProperties.LAST_NAME)),
      email: val => runValidators(val, personValidators(UserProperties.EMAIL)),
      new_password: val => runValidators(val, personValidators(UserProperties.PASSWORD)),
      phone: val => runValidators(val, personValidators(UserProperties.PHONE)),
      termsofuse: val => runValidators(val, personValidators(UserProperties.TERMSOFUSE)),
      account_name: (val, prev) => {
        if (prev?.type?.value !== 'business') {
          return false;
        }
        return runValidators(val, [CompanySchema.name]);
      },
      address_line1: (val, prev) => {
        if (prev?.type?.value !== 'business') {
          return false;
        }
        return runCompanyValidators(val, prev as RegisterFormState, Company.LINE1);
      },
      postal_code: (val, prev) => {
        if (prev?.type?.value !== 'business') {
          return false;
        }
        return runCompanyValidators(val, prev as RegisterFormState, Company.POSTAL_CODE);
      },
      locality: (val, prev) => {
        if (prev?.type?.value !== 'business') {
          return false;
        }
        return runValidators(val, [CompanySchema[Company.LOCALITY]]);
      },
      country_code: (val, prev) => {
        if (prev?.type?.value !== 'business') {
          return false;
        }
        return runValidators(val, [CompanySchema[Company.COUNTRY]]);
      },
    },
  });

  const didPrefill = React.useRef(false);
  React.useEffect(() => {
    const t = urlParser().get('business') ? 'business' : 'private';
    dispatch(actions.setAccountType(t));
  }, [dispatch]);

  React.useEffect(() => {
    if (didPrefill.current || !userId || !userDetails) {
      return;
    }
    didPrefill.current = true;
    form.set({
      first_name: userDetails.first_name ?? '',
      last_name: userDetails.last_name ?? '',
      email: userDetails.email ?? '',
      phone: userDetails.phone ?? '',
    });
    // form.set is stable; avoid re-running when the useForm return identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userDetails]);

  const isBusiness = form.data.type.value === 'business';
  const hasErrors = errors !== '';

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (form.hasErrors || submitting) {
      return;
    }
    const values: RegisterFormFields = {
      type: form.data.type.value as AccountType,
      first_name: form.data.first_name.value,
      last_name: form.data.last_name.value,
      email: form.data.email.value,
      new_password: form.data.new_password.value,
      phone: form.data.phone.value,
      termsofuse: form.data.termsofuse.value,
      account_name: form.data.account_name.value,
      address_line1: form.data.address_line1.value,
      postal_code: form.data.postal_code.value,
      locality: form.data.locality.value,
      country_code: form.data.country_code.value,
    };
    dispatch(actions.register(buildRegisterSubmitPayload(values)));
    form.reset();
  };

  return (
    <SectionWrapper extraClasses={'register-wrapper'} sectionStyle={'white'} centerContent={true}>
      <form onSubmit={onSubmit} noValidate>
        <SheetLayoutContentWrapper>
          <SheetLayoutHeader>
            <Heading size={2}>
              <Translate id={'authentication.register.headline'} />
            </Heading>
          </SheetLayoutHeader>
          <SheetLayoutContent>
            <p>
              <Translate id={'authentication.login.brief'} />
              <span
                className={'cursor-pointer trigger underline text-primary'}
                onClick={() => dispatch(push(r.user.login({ trId: urlParser().get('transport_request') })))}
              >
                <Translate id={'authentication.login.button'} />
              </span>
            </p>
            <SocialButtons socialAuth={service => dispatch(actions.socialAuthStart(service, false))} />
            <br />
            <InputSwitch
              className="input-default--space"
              id="register-user-type"
              name="user.type"
              value={form.data.type.value}
              onChange={next => {
                const type = next as AccountType;
                form.set({ type });
                dispatch(actions.setAccountType(type));
              }}
              options={[
                {
                  value: 'private',
                  label: <Translate id={'request_flow.contact.is_private'} />,
                },
                {
                  value: 'business',
                  label: <Translate id={'request_flow.contact.is_business'} />,
                },
              ]}
            />
            {isBusiness && <CompanyDetails form={form as UseForm.Form<RegisterFormFields>} />}
            <PersonalDetails form={form as UseForm.Form<RegisterFormFields>} />
            <Row>
              <Col xs={12} sm={12}>
                {hasErrors && <FlashMessage type={'error'} message={errors} />}
                <Button
                  fullWidth={true}
                  loading={submitting}
                  disabled={submitting || !form.isDirty || form.hasErrors}
                  buttonStyle={'secondary'}
                  type="submit"
                >
                  <Translate id={'authentication.register.button'} />
                </Button>
              </Col>
            </Row>
          </SheetLayoutContent>
        </SheetLayoutContentWrapper>
      </form>
    </SectionWrapper>
  );
};
